// ── Friet Fighter — super-cinematics (render-only) ──
// Een bespoke "cut scene" van enkele seconden bij een super-treffer. Twee tiers:
//   • 'hit'  — super raakt maar killt niet → korte punch (~1,3s).
//   • 'kill' — super is de doodsklap        → volledig uitgemeten (~2,6s) + KO-hold.
//
// DETERMINISME: de sim bevriest tijdens de cinematic (main.ts zet simRate→0); de
// treffer/KO is dán al door de sim toegepast. Deze director LEEST nooit de sim en
// SCHRIJFT er nooit naar — hij animeert puur de render-laag (attacker-pose via een
// pose-override, een prop aan een hand-bone, en de camera). Net als de klassieke
// Street-Fighter super-freeze: beeld stopt, cinematic speelt, daarna draait de sim
// door. Geen nieuwe MatchState-velden → replay-/rollback-veilig.

import * as THREE from 'three';
import type { Scene3D } from './scene';
import type { BoneDelta } from './poses';
import { CinematicFxRunner, type FxSpec } from './cinematicFx';

export type CineTier = 'hit' | 'kill';

/** Camera-pose op een moment in de cinematic (wereld-coördinaten). */
export interface CinePose {
  px: number; py: number; pz: number; // camera-positie
  tx: number; ty: number; tz: number; // kijkpunt
  fov: number;
}

/** Eén swing-keyframe: een set bone-delta's op genormaliseerde tijd u (0..1). */
interface SwingKey {
  u: number;
  deltas: BoneDelta[];
}

/** Wereld-geplaatste prop (bv. de prikker): de director richt 'm elke frame langs de onderarm. */
interface PropMount {
  scale: number;
  build: () => THREE.Object3D;
}

interface CinematicDef {
  prop?: PropMount;
  squash: number;
  swing: SwingKey[];
  fx?: FxSpec;
  /** Camera-pad als functie van fase u (0..1), attacker-x en facing (+1/−1). */
  camera: (u: number, atkX: number, facing: number) => CinePose;
}

const D = (bone: string, axis: [number, number, number], angle: number): BoneDelta => ({ bone, axis, angle });

// Tier-timing (seconden). De kill speelt dezelfde swing trager + extra KO-hold.
const DUR: Record<CineTier, number> = { hit: 1.3, kill: 2.6 };
// Fractie van de duur dat de swing-animatie beslaat; de rest = aanloop/uitloop-hold.
const SWING_SPAN: Record<CineTier, [number, number]> = { hit: [0.12, 0.78], kill: [0.1, 0.62] };

// ── Berenhap — de prikker-swing (saté-prikker als bo-staff) ──────────────────
// Tweehands-grip, grote sagittale veeg: hoog-achter (windup) → laag-vóór (smash).
// Assen uit de lab-kalibratie (zie memory reference_procedural_animation):
//   RightArm Z− = arm vooruit, X+ = omhoog; ForeArm X− = elleboog strekken;
//   LeftArm spiegelt op Z; Spine X+ = voorwaartse lean; UpLeg X spiegelt L/R.
const berenhapSwing: SwingKey[] = [
  {
    u: 0, // windup: prikker hoog over de schouder naar achter, lijf gespannen terug
    deltas: [
      D('Spine', [1, 0, 0], -0.32),
      D('RightArm', [1, 0, 0], 1.5), D('RightArm', [0, 0, 1], 0.5),
      D('LeftArm', [1, 0, 0], 1.5), D('LeftArm', [0, 0, 1], -0.5),
      D('RightForeArm', [1, 0, 0], -0.35), D('LeftForeArm', [1, 0, 0], -0.35),
      D('RightUpLeg', [1, 0, 0], 0.3), D('LeftUpLeg', [1, 0, 0], 0.3),
      D('RightLeg', [1, 0, 0], 0.4), D('LeftLeg', [1, 0, 0], 0.4),
    ],
  },
  {
    u: 0.4, // door-zwaai: prikker komt hoog naar voren door (arc over de top)
    deltas: [
      D('Spine', [1, 0, 0], 0.12),
      D('RightArm', [1, 0, 0], 0.5), D('RightArm', [0, 0, 1], -1.3),
      D('LeftArm', [1, 0, 0], 0.5), D('LeftArm', [0, 0, 1], 1.3),
      D('RightForeArm', [1, 0, 0], -0.5), D('LeftForeArm', [1, 0, 0], -0.5),
      D('RightUpLeg', [1, 0, 0], 0.2), D('LeftUpLeg', [1, 0, 0], 0.5),
      D('RightLeg', [1, 0, 0], 0.3), D('LeftLeg', [1, 0, 0], 0.5),
    ],
  },
  {
    u: 0.6, // contact: prikker volledig vooruit gestoken (piek-reach), diepe lunge
    deltas: [
      D('Spine', [1, 0, 0], 0.44),
      D('RightArm', [1, 0, 0], 0.0), D('RightArm', [0, 0, 1], -1.7),
      D('LeftArm', [1, 0, 0], 0.0), D('LeftArm', [0, 0, 1], 1.7),
      D('RightForeArm', [1, 0, 0], -0.78), D('LeftForeArm', [1, 0, 0], -0.78),
      D('RightUpLeg', [1, 0, 0], 0.2), D('LeftUpLeg', [1, 0, 0], 0.78),
      D('RightLeg', [1, 0, 0], 0.3), D('LeftLeg', [1, 0, 0], 0.55),
    ],
  },
  {
    u: 1, // follow-through: de prikker zwaait laag-vooruit door (arc afgemaakt)
    deltas: [
      D('Spine', [1, 0, 0], 0.3),
      D('RightArm', [1, 0, 0], -0.6), D('RightArm', [0, 0, 1], -1.05),
      D('LeftArm', [1, 0, 0], -0.6), D('LeftArm', [0, 0, 1], 1.05),
      D('RightForeArm', [1, 0, 0], -0.5), D('LeftForeArm', [1, 0, 0], -0.5),
      D('RightUpLeg', [1, 0, 0], 0.2), D('LeftUpLeg', [1, 0, 0], 0.6),
      D('RightLeg', [1, 0, 0], 0.3), D('LeftLeg', [1, 0, 0], 0.5),
    ],
  },
];

// ── Kroket — de friteuse-launch (barst omhoog uit de frituur) ────────────────
// Vignette: Kroket schiet uit de friteuse-mand omhoog in een wolk stoom + olie/
// kruimels. Diepe hurk-windup (squash) → explosieve opwaartse stretch → landing.
// De kruimel/stoom-burst (zie fx) vuurt op de launch.
const kroketSwing: SwingKey[] = [
  {
    u: 0, // windup: diep ineengedoken in de mand, armen ingetrokken
    deltas: [
      D('Spine', [1, 0, 0], 0.35),
      D('RightArm', [0, 0, 1], -0.5), D('LeftArm', [0, 0, 1], 0.5),
      D('RightForeArm', [1, 0, 0], -1.1), D('LeftForeArm', [1, 0, 0], -1.1),
      D('RightUpLeg', [1, 0, 0], 0.8), D('LeftUpLeg', [1, 0, 0], 0.8),
      D('RightLeg', [1, 0, 0], 1.0), D('LeftLeg', [1, 0, 0], 1.0),
    ],
  },
  {
    u: 0.4, // LAUNCH: explosief omhoog gestrekt, armen wijd-omhoog
    deltas: [
      D('Spine', [1, 0, 0], -0.15),
      D('RightArm', [1, 0, 0], 1.5), D('RightArm', [0, 0, 1], -0.6),
      D('LeftArm', [1, 0, 0], 1.5), D('LeftArm', [0, 0, 1], 0.6),
      D('RightForeArm', [1, 0, 0], -0.15), D('LeftForeArm', [1, 0, 0], -0.15),
      D('RightUpLeg', [1, 0, 0], -0.1), D('LeftUpLeg', [1, 0, 0], -0.1),
      D('RightLeg', [1, 0, 0], 0.1), D('LeftLeg', [1, 0, 0], 0.1),
    ],
  },
  {
    u: 1, // top: uitgestrekte zege-pose, armen hoog
    deltas: [
      D('Spine', [1, 0, 0], 0.0),
      D('RightArm', [1, 0, 0], 1.2), D('RightArm', [0, 0, 1], -0.8),
      D('LeftArm', [1, 0, 0], 1.2), D('LeftArm', [0, 0, 1], 0.8),
      D('RightForeArm', [1, 0, 0], -0.2), D('LeftForeArm', [1, 0, 0], -0.2),
      D('RightUpLeg', [1, 0, 0], 0.15), D('LeftUpLeg', [1, 0, 0], 0.15),
      D('RightLeg', [1, 0, 0], 0.2), D('LeftLeg', [1, 0, 0], 0.2),
    ],
  },
];

// ── Bamiblok — de mie-smash (mie spat alle kanten op) ────────────────────────
// Vignette: Bamiblok stampt/smasht en mie-slierten vliegen radiaal naar buiten.
// Hoog opheffen → harde dubbele neerwaartse smash → lage follow. De radiale
// mie-burst (zie fx) vuurt op de smash.
const bamiblokSwing: SwingKey[] = [
  {
    u: 0, // windup: beide vuisten hoog boven het hoofd, lijf gestrekt terug
    deltas: [
      D('Spine', [1, 0, 0], -0.3),
      D('RightArm', [1, 0, 0], 1.8), D('RightArm', [0, 0, 1], -0.15),
      D('LeftArm', [1, 0, 0], 1.8), D('LeftArm', [0, 0, 1], 0.15),
      D('RightForeArm', [1, 0, 0], -0.15), D('LeftForeArm', [1, 0, 0], -0.15),
      D('RightUpLeg', [1, 0, 0], 0.3), D('LeftUpLeg', [1, 0, 0], 0.3),
      D('RightLeg', [1, 0, 0], 0.4), D('LeftLeg', [1, 0, 0], 0.4),
    ],
  },
  {
    u: 0.42, // SMASH: dubbele vuist-stamp omlaag, lijf klapt voorover (squash)
    deltas: [
      D('Spine', [1, 0, 0], 0.55),
      D('RightArm', [1, 0, 0], -0.7), D('RightArm', [0, 0, 1], -0.5),
      D('LeftArm', [1, 0, 0], -0.7), D('LeftArm', [0, 0, 1], 0.5),
      D('RightForeArm', [1, 0, 0], -0.5), D('LeftForeArm', [1, 0, 0], -0.5),
      D('RightUpLeg', [1, 0, 0], 0.5), D('LeftUpLeg', [1, 0, 0], 0.5),
      D('RightLeg', [1, 0, 0], 0.7), D('LeftLeg', [1, 0, 0], 0.7),
    ],
  },
  {
    u: 1, // follow: laag-voorovergebogen na de stamp
    deltas: [
      D('Spine', [1, 0, 0], 0.4),
      D('RightArm', [1, 0, 0], -0.4), D('RightArm', [0, 0, 1], -0.7),
      D('LeftArm', [1, 0, 0], -0.4), D('LeftArm', [0, 0, 1], 0.7),
      D('RightForeArm', [1, 0, 0], -0.7), D('LeftForeArm', [1, 0, 0], -0.7),
      D('RightUpLeg', [1, 0, 0], 0.4), D('LeftUpLeg', [1, 0, 0], 0.4),
      D('RightLeg', [1, 0, 0], 0.55), D('LeftLeg', [1, 0, 0], 0.55),
    ],
  },
];

// ── Kipcorn — speed-flurry (alternerende snelle jabs) ────────────────────────
// Vignette: snelle bokser. Flurry van afwisselende rechtse/linkse jabs; de
// swoosh op de rechtervuist zigzagt → speed-lines.
const kipcornSwing: SwingKey[] = [
  {
    u: 0, // guard: vuisten hoog bij het gezicht, lichte hurk
    deltas: [
      D('Spine', [1, 0, 0], 0),
      D('RightArm', [0, 0, 1], -0.4), D('RightForeArm', [1, 0, 0], -1.15),
      D('LeftArm', [0, 0, 1], 0.4), D('LeftForeArm', [1, 0, 0], -1.15),
      D('RightUpLeg', [1, 0, 0], 0.3), D('LeftUpLeg', [1, 0, 0], 0.3), D('RightLeg', [1, 0, 0], 0.4), D('LeftLeg', [1, 0, 0], 0.4),
    ],
  },
  {
    u: 0.28, // rechtse jab vooruit
    deltas: [
      D('Spine', [1, 0, 0], 0.15),
      D('RightArm', [0, 0, 1], -1.75), D('RightArm', [1, 0, 0], 0.3), D('RightForeArm', [1, 0, 0], -0.15),
      D('LeftArm', [0, 0, 1], 0.4), D('LeftForeArm', [1, 0, 0], -1.15),
      D('LeftUpLeg', [1, 0, 0], 0.4), D('RightUpLeg', [1, 0, 0], 0.15),
    ],
  },
  {
    u: 0.52, // linkse jab (rechts trekt terug)
    deltas: [
      D('Spine', [1, 0, 0], 0.15),
      D('RightArm', [0, 0, 1], -0.4), D('RightForeArm', [1, 0, 0], -1.15),
      D('LeftArm', [0, 0, 1], 1.75), D('LeftArm', [1, 0, 0], 0.3), D('LeftForeArm', [1, 0, 0], -0.15),
      D('LeftUpLeg', [1, 0, 0], 0.4), D('RightUpLeg', [1, 0, 0], 0.15),
    ],
  },
  {
    u: 0.76, // rechtse jab opnieuw (flurry)
    deltas: [
      D('Spine', [1, 0, 0], 0.2),
      D('RightArm', [0, 0, 1], -1.8), D('RightArm', [1, 0, 0], 0.3), D('RightForeArm', [1, 0, 0], -0.1),
      D('LeftArm', [0, 0, 1], 0.4), D('LeftForeArm', [1, 0, 0], -1.15),
      D('LeftUpLeg', [1, 0, 0], 0.45), D('RightUpLeg', [1, 0, 0], 0.15),
    ],
  },
  {
    u: 1, // recover naar guard
    deltas: [
      D('Spine', [1, 0, 0], 0.05),
      D('RightArm', [0, 0, 1], -0.4), D('RightForeArm', [1, 0, 0], -1.15),
      D('LeftArm', [0, 0, 1], 0.4), D('LeftForeArm', [1, 0, 0], -1.15),
      D('RightUpLeg', [1, 0, 0], 0.3), D('LeftUpLeg', [1, 0, 0], 0.3),
    ],
  },
];

// ── Eierbal — zwevende uppercut (float/air) + witte ei-burst ─────────────────
// Vignette: ronde ei-bal die luchtig bobt/springt. Rijzende uppercut met een
// witte paneer/ei-spray. Lichte stretch (squash 1.1) voor het zwevende gevoel.
const eierbalSwing: SwingKey[] = [
  {
    u: 0, // windup: ineengezakt, rechterarm laag-achter
    deltas: [
      D('Spine', [1, 0, 0], 0.2),
      D('RightArm', [0, 0, 1], 0.3), D('RightArm', [1, 0, 0], -0.4), D('RightForeArm', [1, 0, 0], -0.6),
      D('LeftArm', [0, 0, 1], 0.4), D('LeftForeArm', [1, 0, 0], -0.9),
      D('RightUpLeg', [1, 0, 0], 0.5), D('LeftUpLeg', [1, 0, 0], 0.5), D('RightLeg', [1, 0, 0], 0.6), D('LeftLeg', [1, 0, 0], 0.6),
    ],
  },
  {
    u: 0.45, // uppercut: rechterarm zwiept omhoog-vooruit, lijf rijst
    deltas: [
      D('Spine', [1, 0, 0], -0.15),
      D('RightArm', [0, 0, 1], -0.7), D('RightArm', [1, 0, 0], 1.3), D('RightForeArm', [1, 0, 0], -0.2),
      D('LeftArm', [0, 0, 1], 0.3), D('LeftForeArm', [1, 0, 0], -1.0),
      D('RightUpLeg', [1, 0, 0], -0.1), D('LeftUpLeg', [1, 0, 0], -0.1), D('RightLeg', [1, 0, 0], 0.15), D('LeftLeg', [1, 0, 0], 0.15),
    ],
  },
  {
    u: 1, // float: vuist hoog, lijf zwevend uitgestrekt
    deltas: [
      D('Spine', [1, 0, 0], -0.05),
      D('RightArm', [0, 0, 1], -0.5), D('RightArm', [1, 0, 0], 1.05), D('RightForeArm', [1, 0, 0], -0.35),
      D('LeftArm', [0, 0, 1], 0.3), D('LeftForeArm', [1, 0, 0], -0.95),
      D('RightUpLeg', [1, 0, 0], 0.1), D('LeftUpLeg', [1, 0, 0], 0.1), D('RightLeg', [1, 0, 0], 0.2), D('LeftLeg', [1, 0, 0], 0.2),
    ],
  },
];

// ── Loempia — evade → riposte (technical/counter) ────────────────────────────
// Vignette: technische bokser/guard. Eerst terug-leunen (counter-dodge), dan een
// scherpe rechtse riposte. Swoosh op de vuist.
const loempiaSwing: SwingKey[] = [
  {
    u: 0, // evade: ver achterover-leunen, armen in dekking
    deltas: [
      D('Spine', [1, 0, 0], -0.5),
      D('RightArm', [0, 0, 1], -0.3), D('RightForeArm', [1, 0, 0], -1.2),
      D('LeftArm', [0, 0, 1], 0.4), D('LeftForeArm', [1, 0, 0], -1.1),
      D('RightUpLeg', [1, 0, 0], -0.3), D('LeftUpLeg', [1, 0, 0], 0.3), D('LeftLeg', [1, 0, 0], 0.4),
    ],
  },
  {
    u: 0.5, // riposte: scherpe rechtse counter recht vooruit, lijf klapt naar voren
    deltas: [
      D('Spine', [1, 0, 0], 0.42),
      D('RightArm', [0, 0, 1], -1.9), D('RightArm', [1, 0, 0], 0.25), D('RightForeArm', [1, 0, 0], -0.1),
      D('LeftArm', [0, 0, 1], -0.3), D('LeftForeArm', [1, 0, 0], -1.0),
      D('LeftUpLeg', [1, 0, 0], 0.7), D('LeftLeg', [1, 0, 0], 0.5), D('RightUpLeg', [1, 0, 0], 0.15),
    ],
  },
  {
    u: 1, // recover: vuist nog vooruit, lijf settelt
    deltas: [
      D('Spine', [1, 0, 0], 0.25),
      D('RightArm', [0, 0, 1], -1.5), D('RightArm', [1, 0, 0], 0.2), D('RightForeArm', [1, 0, 0], -0.3),
      D('LeftArm', [0, 0, 1], -0.2), D('LeftForeArm', [1, 0, 0], -1.0),
      D('LeftUpLeg', [1, 0, 0], 0.5), D('LeftLeg', [1, 0, 0], 0.45), D('RightUpLeg', [1, 0, 0], 0.15),
    ],
  },
];

// ── Kaassoufflé — twin vuur-/kaasstralen (zoner, armen wijd → blast) ─────────
// Vignette: armen wijd, twee stralen gesmolten kaas/vuur blazen naar weerszijden.
// Windup (armen in) → explosieve wijde cast (armen vooruit-omhoog) → hold. De
// twin jets (zie fx) blazen op de cast vanaf het lijf langs ±Z (scherm-zijwaarts).
const kaassouffleSwing: SwingKey[] = [
  {
    u: 0, // windup: armen ingetrokken voor de borst, lichte hurk, lijf terug
    deltas: [
      D('Spine', [1, 0, 0], -0.24),
      D('RightArm', [0, 0, 1], 0.35), D('RightArm', [1, 0, 0], 0.25), D('RightForeArm', [1, 0, 0], -1.0),
      D('LeftArm', [0, 0, 1], -0.35), D('LeftArm', [1, 0, 0], 0.25), D('LeftForeArm', [1, 0, 0], -1.0),
      D('RightUpLeg', [1, 0, 0], 0.35), D('LeftUpLeg', [1, 0, 0], 0.35), D('RightLeg', [1, 0, 0], 0.4), D('LeftLeg', [1, 0, 0], 0.4),
    ],
  },
  {
    u: 0.4, // cast: armen explosief wijd-vooruit-omhoog gegooid (blast-pose)
    deltas: [
      D('Spine', [1, 0, 0], 0.12),
      D('RightArm', [0, 0, 1], -1.3), D('RightArm', [1, 0, 0], 0.75), D('RightForeArm', [1, 0, 0], -0.2),
      D('LeftArm', [0, 0, 1], 1.3), D('LeftArm', [1, 0, 0], 0.75), D('LeftForeArm', [1, 0, 0], -0.2),
      D('RightUpLeg', [1, 0, 0], 0.2), D('LeftUpLeg', [1, 0, 0], 0.2), D('RightLeg', [1, 0, 0], 0.35), D('LeftLeg', [1, 0, 0], 0.35),
    ],
  },
  {
    u: 1, // hold: armen wijd-vooruit, lichte daling (na-blast)
    deltas: [
      D('Spine', [1, 0, 0], 0.05),
      D('RightArm', [0, 0, 1], -1.05), D('RightArm', [1, 0, 0], 0.55), D('RightForeArm', [1, 0, 0], -0.35),
      D('LeftArm', [0, 0, 1], 1.05), D('LeftArm', [1, 0, 0], 0.55), D('LeftForeArm', [1, 0, 0], -0.35),
      D('RightUpLeg', [1, 0, 0], 0.25), D('LeftUpLeg', [1, 0, 0], 0.25), D('RightLeg', [1, 0, 0], 0.35), D('LeftLeg', [1, 0, 0], 0.35),
    ],
  },
];

// ── Bitterbal — de saus-eruptie (grappler-slam → ketchup-geiser + flessen) ───
// Vignette: Bitterbal stampt en een ketchup-geiser barst omhoog, sauzen spatten
// alle kanten op. Beide groene vuisten hoog → harde grond-stamp → lage follow.
const bitterbalSwing: SwingKey[] = [
  {
    u: 0, // windup: vuisten hoog, lijf terug, lichte hurk
    deltas: [
      D('Spine', [1, 0, 0], -0.25),
      D('RightArm', [1, 0, 0], 1.65), D('RightArm', [0, 0, 1], -0.2),
      D('LeftArm', [1, 0, 0], 1.65), D('LeftArm', [0, 0, 1], 0.2),
      D('RightForeArm', [1, 0, 0], -0.2), D('LeftForeArm', [1, 0, 0], -0.2),
      D('RightUpLeg', [1, 0, 0], 0.4), D('LeftUpLeg', [1, 0, 0], 0.4),
      D('RightLeg', [1, 0, 0], 0.5), D('LeftLeg', [1, 0, 0], 0.5),
    ],
  },
  {
    u: 0.42, // STAMP: dubbele vuist-stamp omlaag, diepe hurk, lijf voorover
    deltas: [
      D('Spine', [1, 0, 0], 0.5),
      D('RightArm', [1, 0, 0], -0.9), D('RightArm', [0, 0, 1], -0.35),
      D('LeftArm', [1, 0, 0], -0.9), D('LeftArm', [0, 0, 1], 0.35),
      D('RightForeArm', [1, 0, 0], -0.4), D('LeftForeArm', [1, 0, 0], -0.4),
      D('RightUpLeg', [1, 0, 0], 0.75), D('LeftUpLeg', [1, 0, 0], 0.75),
      D('RightLeg', [1, 0, 0], 0.95), D('LeftLeg', [1, 0, 0], 0.95),
    ],
  },
  {
    u: 1, // follow: laag-voorovergebogen na de stamp
    deltas: [
      D('Spine', [1, 0, 0], 0.38),
      D('RightArm', [1, 0, 0], -0.6), D('RightArm', [0, 0, 1], -0.5),
      D('LeftArm', [1, 0, 0], -0.6), D('LeftArm', [0, 0, 1], 0.5),
      D('RightForeArm', [1, 0, 0], -0.5), D('LeftForeArm', [1, 0, 0], -0.5),
      D('RightUpLeg', [1, 0, 0], 0.6), D('LeftUpLeg', [1, 0, 0], 0.6),
      D('RightLeg', [1, 0, 0], 0.75), D('LeftLeg', [1, 0, 0], 0.75),
    ],
  },
];

// ── Frikandel — de boks-cross (rushdown-bokser, machtige rechte) ─────────────
// Vignette: Frikandel komt binnen en gooit een grote rechtse cross. ASYMMETRISCH
// (rechts stoot, links dekt) → niet L/R-gespiegeld. Swoosh op de vuist (RightHand).
const frikandelSwing: SwingKey[] = [
  {
    u: 0, // windup: rechterhandschoen ver naar achter+omhoog gecockt, links in dekking, gewicht terug
    deltas: [
      D('Spine', [1, 0, 0], -0.25),
      D('RightArm', [0, 0, 1], 0.85), D('RightArm', [1, 0, 0], 0.55), D('RightForeArm', [1, 0, 0], -1.3),
      D('LeftArm', [0, 0, 1], 0.55), D('LeftForeArm', [1, 0, 0], -0.95),
      D('RightUpLeg', [1, 0, 0], 0.45), D('LeftUpLeg', [1, 0, 0], -0.25), D('RightLeg', [1, 0, 0], 0.45),
    ],
  },
  {
    u: 0.5, // contact: explosieve rechtse cross volledig vooruit op schouderhoogte, arm gestrekt
    deltas: [
      D('Spine', [1, 0, 0], 0.32),
      D('RightArm', [0, 0, 1], -1.95), D('RightArm', [1, 0, 0], 0.4), D('RightForeArm', [1, 0, 0], -0.05),
      D('LeftArm', [0, 0, 1], -0.35), D('LeftForeArm', [1, 0, 0], -1.05),
      D('LeftUpLeg', [1, 0, 0], 0.75), D('LeftLeg', [1, 0, 0], 0.5), D('RightUpLeg', [1, 0, 0], 0.1),
    ],
  },
  {
    u: 1, // follow: vuist blijft hoog-vooruit gestrekt (silhouet vasthouden), lichte terugtrek
    deltas: [
      D('Spine', [1, 0, 0], 0.22),
      D('RightArm', [0, 0, 1], -1.6), D('RightArm', [1, 0, 0], 0.38), D('RightForeArm', [1, 0, 0], -0.2),
      D('LeftArm', [0, 0, 1], -0.25), D('LeftForeArm', [1, 0, 0], -1.0),
      D('LeftUpLeg', [1, 0, 0], 0.55), D('LeftLeg', [1, 0, 0], 0.45), D('RightUpLeg', [1, 0, 0], 0.1),
    ],
  },
];

// ── Patatje Oorlog (boss) — de "Oerknal" (overhead-slam → sauce+vuur-explosie) ─
// Geen vignette (boss kwam ná de trailer) → grootste cinematic: massieve overhead
// slam die een chaos van patatje-oorlog-sauzen (mayo/curry/ketchup) + vuur ontketent.
const oerknalSwing: SwingKey[] = [
  {
    u: 0, // windup: beide armen ver overhead, lijf gestrekt achterover, brede stand
    deltas: [
      D('Spine', [1, 0, 0], -0.38),
      D('RightArm', [1, 0, 0], 1.85), D('RightArm', [0, 0, 1], -0.15),
      D('LeftArm', [1, 0, 0], 1.85), D('LeftArm', [0, 0, 1], 0.15),
      D('RightForeArm', [1, 0, 0], -0.15), D('LeftForeArm', [1, 0, 0], -0.15),
      D('RightUpLeg', [1, 0, 0], 0.4), D('LeftUpLeg', [1, 0, 0], 0.4), D('RightLeg', [1, 0, 0], 0.5), D('LeftLeg', [1, 0, 0], 0.5),
    ],
  },
  {
    u: 0.4, // OERKNAL: dubbele slam omlaag met volle kracht, diepe hurk, lijf voorover
    deltas: [
      D('Spine', [1, 0, 0], 0.6),
      D('RightArm', [1, 0, 0], -0.95), D('RightArm', [0, 0, 1], -0.55),
      D('LeftArm', [1, 0, 0], -0.95), D('LeftArm', [0, 0, 1], 0.55),
      D('RightForeArm', [1, 0, 0], -0.35), D('LeftForeArm', [1, 0, 0], -0.35),
      D('RightUpLeg', [1, 0, 0], 0.8), D('LeftUpLeg', [1, 0, 0], 0.8), D('RightLeg', [1, 0, 0], 1.0), D('LeftLeg', [1, 0, 0], 1.0),
    ],
  },
  {
    u: 1, // follow: laag-voorovergebogen na de inslag
    deltas: [
      D('Spine', [1, 0, 0], 0.42),
      D('RightArm', [1, 0, 0], -0.6), D('RightArm', [0, 0, 1], -0.7),
      D('LeftArm', [1, 0, 0], -0.6), D('LeftArm', [0, 0, 1], 0.7),
      D('RightForeArm', [1, 0, 0], -0.6), D('LeftForeArm', [1, 0, 0], -0.6),
      D('RightUpLeg', [1, 0, 0], 0.55), D('LeftUpLeg', [1, 0, 0], 0.55), D('RightLeg', [1, 0, 0], 0.7), D('LeftLeg', [1, 0, 0], 0.7),
    ],
  },
];

/** Bouw de prikker: brons-stok + punt + drie gehakt-kralen (berenhap = balletjes aan een prikker). */
function buildPrikker(): THREE.Object3D {
  const g = new THREE.Group();
  const brass = new THREE.MeshStandardMaterial({ color: 0xb5832e, roughness: 0.35, metalness: 0.85 });
  const meat = new THREE.MeshStandardMaterial({ color: 0x6b3b1e, roughness: 0.85, metalness: 0.05 });
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 1.5, 10), brass);
  shaft.rotation.z = Math.PI / 2; // langs de lokale X-as leggen
  g.add(shaft);
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.16, 10), brass);
  tip.rotation.z = -Math.PI / 2;
  tip.position.x = 0.82;
  g.add(tip);
  for (let i = 0; i < 3; i++) {
    const bead = new THREE.Mesh(new THREE.SphereGeometry(0.12, 14, 10), meat);
    bead.position.x = -0.1 - i * 0.26;
    bead.scale.set(1.1, 0.95, 0.95);
    g.add(bead);
  }
  return g;
}

// Dramatische 3/4-front camera die indolt op de attacker; kill duwt verder door.
function arenaCamera(u: number, atkX: number, facing: number, tier: CineTier): CinePose {
  const zoom = tier === 'kill' ? 0.55 + u * 0.5 : 0.4 + u * 0.35; // [0..~1]
  const pz = THREE.MathUtils.lerp(4.6, tier === 'kill' ? 2.7 : 3.4, Math.min(1, zoom));
  const fov = THREE.MathUtils.lerp(36, tier === 'kill' ? 28 : 31, Math.min(1, zoom));
  const px = atkX + facing * THREE.MathUtils.lerp(2.6, 1.7, Math.min(1, zoom));
  return {
    px, py: THREE.MathUtils.lerp(1.7, 1.45, Math.min(1, zoom)), pz,
    tx: atkX + facing * 0.45, ty: 1.18, tz: 0, fov,
  };
}

export const CINEMATICS: Record<string, CinematicDef> = {
  berenhap: {
    squash: 1,
    swing: berenhapSwing,
    prop: { scale: 0.8, build: buildPrikker },
    fx: { trail: { color: 0xffd86b, width: 0.24 } }, // swoosh langs de prikker-tip
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'hit'),
  },
  kroket: {
    squash: 1.12, // lichte stretch op de launch
    swing: kroketSwing,
    fx: {
      originBone: 'Spine',
      bursts: [
        { u: 0.38, kind: 'crumb', count: 26, speed: 4.2, spread: 'up', color: 0xc98a3a },
        { u: 0.38, kind: 'steam', count: 7, speed: 1.4, spread: 'up' },
      ],
    },
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'hit'),
  },
  bamiblok: {
    squash: 1,
    swing: bamiblokSwing,
    fx: {
      originBone: 'Hips',
      bursts: [{ u: 0.42, kind: 'noodle', count: 30, speed: 4.6, spread: 'radial', color: 0xd9c060 }],
    },
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'hit'),
  },
  frikandel: {
    squash: 1,
    swing: frikandelSwing,
    fx: {
      trail: { color: 0xffcaa0, width: 0.2, bone: 'RightHand' }, // swoosh op de vuist
      originBone: 'RightHand',
      bursts: [{ u: 0.52, kind: 'crumb', count: 10, speed: 3.2, spread: 'radial', color: 0x8a4a1e }], // impact-specks
    },
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'hit'),
  },
  patatje_oorlog: {
    squash: 1,
    swing: oerknalSwing,
    fx: {
      originBone: 'Hips',
      bursts: [
        { u: 0.4, kind: 'fire', count: 34, speed: 8.0, spread: 'radial', color: 0xff8a20 }, // de Oerknal-vlam
        { u: 0.4, kind: 'fire', count: 16, speed: 6.5, spread: 'up', color: 0xffd24a }, // gele kern omhoog
        { u: 0.42, kind: 'sauce', count: 20, speed: 6.0, spread: 'up', color: 0xc81e14 }, // ketchup-geiser
        { u: 0.42, kind: 'sauce', count: 16, speed: 5.2, spread: 'radial', color: 0xf0e6c8 }, // mayo
        { u: 0.44, kind: 'sauce', count: 14, speed: 4.8, spread: 'radial', color: 0x9a5a1e }, // curry
        { u: 0.4, kind: 'steam', count: 9, speed: 1.6, spread: 'up' }, // dampwolk
      ],
    },
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'kill'), // boss = diepste/grootste kader
  },
  kipcorn: {
    squash: 1,
    swing: kipcornSwing,
    fx: {
      trail: { color: 0xcfe8ff, width: 0.18, bone: 'RightHand' }, // koel-witte speed-swoosh
      originBone: 'RightHand',
      bursts: [{ u: 0.78, kind: 'crumb', count: 8, speed: 3.0, spread: 'radial', color: 0xd8a850 }],
    },
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'hit'),
  },
  eierbal: {
    squash: 1.1, // licht zwevend/uitgerekt
    swing: eierbalSwing,
    fx: {
      originBone: 'Spine',
      bursts: [
        { u: 0.45, kind: 'crumb', count: 18, speed: 4.0, spread: 'up', color: 0xf0ead8 }, // witte ei/paneer-spray
        { u: 0.46, kind: 'steam', count: 4, speed: 1.0, spread: 'up' },
      ],
    },
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'hit'),
  },
  loempia: {
    squash: 1,
    swing: loempiaSwing,
    fx: {
      trail: { color: 0xffd0a0, width: 0.2, bone: 'RightHand' }, // warme counter-swoosh
      originBone: 'RightHand',
      bursts: [{ u: 0.52, kind: 'crumb', count: 9, speed: 3.2, spread: 'radial', color: 0xb5803a }],
    },
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'hit'),
  },
  kaassouffle: {
    squash: 1,
    swing: kaassouffleSwing,
    fx: {
      originBone: 'Spine',
      bursts: [
        { u: 0.4, kind: 'fire', count: 22, speed: 7.0, spread: 'jet', dir: [1, 0.14, 0], color: 0xffa030 }, // straal naar één kant (langs de baan)
        { u: 0.4, kind: 'fire', count: 22, speed: 7.0, spread: 'jet', dir: [-1, 0.14, 0], color: 0xffa030 }, // straal naar de andere kant
        { u: 0.42, kind: 'fire', count: 10, speed: 5.5, spread: 'jet', dir: [1, 0.14, 0], color: 0xf2d24a }, // kaas-geel accent
        { u: 0.42, kind: 'fire', count: 10, speed: 5.5, spread: 'jet', dir: [-1, 0.14, 0], color: 0xf2d24a },
      ],
    },
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'hit'),
  },
  bitterbal: {
    squash: 1,
    swing: bitterbalSwing,
    fx: {
      originBone: 'Hips',
      bursts: [
        { u: 0.42, kind: 'sauce', count: 26, speed: 5.0, spread: 'up', color: 0xc81e14 }, // ketchup-geiser
        { u: 0.42, kind: 'sauce', count: 16, speed: 4.2, spread: 'radial', color: 0xf0e6c8 }, // mayo-spatten
        { u: 0.44, kind: 'sauce', count: 8, speed: 3.6, spread: 'radial', color: 0x9a5a1e }, // curry-klodders
      ],
    },
    camera: (u, atkX, f) => arenaCamera(u, atkX, f, 'hit'),
  },
};

/** Lineair lerp tussen twee swing-keyframes op (bone,axis)-niveau (ontbrekend = 0). */
function lerpKeys(a: SwingKey, b: SwingKey, t: number): BoneDelta[] {
  const out: BoneDelta[] = [];
  const seen = new Set<string>();
  const push = (src: SwingKey, other: SwingKey): void => {
    for (const d of src.deltas) {
      const key = d.bone + d.axis.join(',');
      if (seen.has(key)) continue;
      seen.add(key);
      const o = other.deltas.find((x) => x.bone === d.bone && x.axis.join(',') === d.axis.join(','));
      const from = src === a ? d.angle : o?.angle ?? 0;
      const to = src === a ? o?.angle ?? 0 : d.angle;
      out.push({ bone: d.bone, axis: d.axis, angle: from + (to - from) * t });
    }
  };
  push(a, b);
  push(b, a);
  return out;
}

/** Bepaal de swing-pose (bone-delta's) op genormaliseerde swing-fase u (0..1). PUUR. */
export function swingPoseAt(swing: SwingKey[], u: number): BoneDelta[] {
  const c = THREE.MathUtils.clamp(u, 0, 1);
  for (let i = 0; i < swing.length - 1; i++) {
    const a = swing[i];
    const b = swing[i + 1];
    if (c <= b.u || i === swing.length - 2) {
      const span = b.u - a.u || 1;
      return lerpKeys(a, b, THREE.MathUtils.clamp((c - a.u) / span, 0, 1));
    }
  }
  return swing[0]?.deltas ?? [];
}

/** Is er een cinematic voor deze charId? (main.ts beslist daarop wel/niet triggeren.) */
export function hasCinematic(charId: string): boolean {
  return charId in CINEMATICS;
}

export class CinematicDirector {
  // Scratch (geen per-frame allocaties) voor de wereld-prop-plaatsing + FX.
  private static readonly handPos = new THREE.Vector3();
  private static readonly elbowPos = new THREE.Vector3();
  private static readonly dir = new THREE.Vector3();
  private static readonly localX = new THREE.Vector3(1, 0, 0);
  private static readonly tipVec = new THREE.Vector3();
  private static readonly originVec = new THREE.Vector3();

  private def: CinematicDef | null = null;
  private fxRunner: CinematicFxRunner | null = null;
  private tier: CineTier = 'hit';
  private atkIndex = 0;
  private atkX = 0;
  private facing = 1;
  private t = 0;
  private dur = 0;
  private prop: THREE.Object3D | null = null;
  private forcedU: number | null = null; // dev: bevries de swing op een vaste fase (kalibratie)

  get active(): boolean {
    return this.def !== null;
  }

  /** Start een cinematic voor de attacker. Geen def → false (normale flow blijft). */
  trigger(scene: Scene3D, attackerIndex: number, charId: string, tier: CineTier, atkX: number, facing: number): boolean {
    if (this.active) return false;
    const def = CINEMATICS[charId];
    if (!def) return false;
    this.def = def;
    this.tier = tier;
    this.atkIndex = attackerIndex;
    this.atkX = atkX;
    this.facing = facing;
    this.t = 0;
    this.forcedU = null;
    this.dur = DUR[tier];
    if (def.prop) {
      this.prop = def.prop.build();
      this.prop.scale.setScalar(def.prop.scale);
      scene.addObject(this.prop); // wereld-geplaatst; elke frame langs de onderarm gericht
    }
    this.fxRunner = def.fx ? new CinematicFxRunner(def.fx) : null;
    if (this.fxRunner) scene.addObject(this.fxRunner.group);
    return true;
  }

  /** Hoofdvoortgang (0..1) over de hele cinematic. */
  progress(): number {
    return this.dur > 0 ? THREE.MathUtils.clamp(this.t / this.dur, 0, 1) : 1;
  }

  /** Swing-fase (0..1) binnen het swing-venster van deze tier. */
  private swingU(): number {
    const [a, b] = SWING_SPAN[this.tier];
    return THREE.MathUtils.clamp((this.progress() - a) / (b - a || 1), 0, 1);
  }

  /** Dev: start de cinematic maar bevries op swing-fase u (prop-/pose-kalibratie). */
  triggerHold(scene: Scene3D, attackerIndex: number, charId: string, u: number, atkX: number, facing: number): boolean {
    const ok = this.trigger(scene, attackerIndex, charId, 'kill', atkX, facing);
    if (ok) this.forcedU = THREE.MathUtils.clamp(u, 0, 1);
    return ok;
  }

  update(dt: number, scene: Scene3D): void {
    if (!this.def) return;
    this.t += dt;
    const held = this.forcedU !== null;
    const u = held ? (this.forcedU as number) : this.swingU();
    const prog = held ? (this.forcedU as number) : this.progress();
    // Attacker-pose: bespoke swing (override op de normale procedurele pose).
    scene.setCharPose(this.atkIndex, swingPoseAt(this.def.swing, u), this.def.squash);
    // Prikker: lees hand + elleboog (wereld) en richt de stok langs de onderarm,
    // tip naar voren voorbij de hand. Volgt zo de swing zonder bone-lokale assen.
    if (this.prop && this.def.prop) {
      const okH = scene.boneWorldPos(this.atkIndex, 'RightHand', CinematicDirector.handPos);
      const okE = scene.boneWorldPos(this.atkIndex, 'RightForeArm', CinematicDirector.elbowPos);
      if (okH && okE) {
        CinematicDirector.dir.copy(CinematicDirector.handPos).sub(CinematicDirector.elbowPos);
        if (CinematicDirector.dir.lengthSq() > 1e-6) {
          CinematicDirector.dir.normalize();
          this.prop.position.copy(CinematicDirector.handPos).addScaledVector(CinematicDirector.dir, 0.42);
          this.prop.quaternion.setFromUnitVectors(CinematicDirector.localX, CinematicDirector.dir);
        }
      }
    }
    // FX: swoosh-trail volgt de prikker-tip; bursts vuren op hun swing-fase vanaf
    // de origin-bone (val terug op een vast punt vóór de vechter).
    if (this.fxRunner) {
      let tip: THREE.Vector3 | null = null;
      const tb = this.def.fx?.trail?.bone;
      if (tb) {
        if (scene.boneWorldPos(this.atkIndex, tb, CinematicDirector.tipVec)) tip = CinematicDirector.tipVec;
      } else if (this.prop) {
        tip = CinematicDirector.tipVec.copy(CinematicDirector.handPos).addScaledVector(CinematicDirector.dir, 1.0);
      }
      const ob = this.def.fx?.originBone;
      if (!ob || !scene.boneWorldPos(this.atkIndex, ob, CinematicDirector.originVec)) {
        CinematicDirector.originVec.set(this.atkX, 1.2, 0);
      }
      this.fxRunner.update(u, dt, tip, CinematicDirector.originVec);
    }
    const cam = this.tier === 'kill'
      ? arenaCamera(prog, this.atkX, this.facing, 'kill')
      : this.def.camera(prog, this.atkX, this.facing);
    scene.setCineCam(cam);
    if (!held && this.t >= this.dur) this.finish(scene);
  }

  /** Beëindig direct (cleanup) — dev/release. */
  release(scene: Scene3D): void {
    if (this.def) this.finish(scene);
  }

  private finish(scene: Scene3D): void {
    this.forcedU = null;
    if (this.prop) scene.removeObject(this.prop);
    this.prop = null;
    if (this.fxRunner) {
      this.fxRunner.clear();
      scene.removeObject(this.fxRunner.group);
      this.fxRunner = null;
    }
    scene.clearCharPose(this.atkIndex);
    scene.setCineCam(null);
    this.def = null;
  }
}
